// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts'

interface LearningObjective {
  objective_statement: string
  bloom_level?: string
  coverage_depth?: 'H' | 'M' | 'L'
  coverage_depth_rationale?: string
  coverage_quality?: string
  points_allocated?: number
  problems_aligned?: string[]
}

interface Props {
  objectives: LearningObjective[]
  documentCount?: number
}

const COVERAGE_CONFIG = {
  H: { label: 'High',   color: '#e21833', value: 3 },
  M: { label: 'Medium', color: '#7f7f7f', value: 2 },
  L: { label: 'Low',    color: '#ffd200', value: 1 },
} as const

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  const cfg = COVERAGE_CONFIG[d.coverage_depth as 'H' | 'M' | 'L']
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-4 max-w-xs">
      <p className="font-semibold text-gray-900 text-sm mb-2 leading-snug">{d.fullName}</p>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: cfg?.color ?? '#9CA3AF' }}
        >
          {d.coverage_depth} — {cfg?.label ?? 'Unknown'}
        </span>
        {d.bloom_level && (
          <span className="text-xs text-gray-500">{d.bloom_level}</span>
        )}
      </div>
      {d.rationale && (
        <p className="text-xs text-gray-600 italic mb-2 leading-relaxed">"{d.rationale}"</p>
      )}
      <div className="space-y-0.5">
        {d.coverage_quality && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">Quality:</span> {d.coverage_quality}
          </p>
        )}
        {d.points > 0 && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">Points:</span> {d.points}
            {d.problems?.length > 0 && ` · Problems: ${d.problems.join(', ')}`}
          </p>
        )}
      </div>
    </div>
  )
}

export function LearningObjectivesCoverageChart({ objectives, documentCount }: Props) {
  const chartData = useMemo(() => {
    return [...objectives]
      .sort((a, b) => {
        const av = COVERAGE_CONFIG[a.coverage_depth as 'H' | 'M' | 'L']?.value ?? 0
        const bv = COVERAGE_CONFIG[b.coverage_depth as 'H' | 'M' | 'L']?.value ?? 0
        return bv - av
      })
      .map(o => {
        const cfg = COVERAGE_CONFIG[o.coverage_depth as 'H' | 'M' | 'L']
        const name = o.objective_statement.length > 50
          ? o.objective_statement.slice(0, 48) + '…'
          : o.objective_statement
        return {
          name,
          fullName: o.objective_statement,
          coverage_depth: o.coverage_depth,
          value: cfg?.value ?? 0,
          color: cfg?.color ?? '#9CA3AF',
          rationale: o.coverage_depth_rationale,
          bloom_level: o.bloom_level,
          coverage_quality: o.coverage_quality,
          points: o.points_allocated ?? 0,
          problems: o.problems_aligned ?? [],
        }
      })
  }, [objectives])

  const counts = useMemo(() => {
    const c = { H: 0, M: 0, L: 0 }
    objectives.forEach(o => {
      if (o.coverage_depth && o.coverage_depth in c) c[o.coverage_depth]++
    })
    return c
  }, [objectives])

  const total = objectives.length
  const chartHeight = Math.max(320, chartData.length * 68)

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid" style={{ overflow: 'visible' }}>
      {/* Header */}
      <h2 className="text-2xl font-bold text-gray-800 mb-1 flex items-center">
        <span className="text-3xl mr-3">📊</span>
        Coverage Depth by Learning Outcome
        {documentCount && documentCount > 1 && (
          <span className="text-sm text-gray-600 ml-3 font-normal">
            (Aggregated across {documentCount} documents)
          </span>
        )}
      </h2>
      <p className="text-xs text-gray-500 mb-6">
        Based on graded assessments (exams, homework, projects). H = High · M = Medium · L = Low
      </p>

      {/* Segmented summary bar */}
      <div className="mb-6">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Coverage Distribution
        </p>
        <div className="flex rounded-full overflow-hidden h-5 w-full">
          {(['H', 'M', 'L'] as const).map(lvl => {
            const pct = (counts[lvl] / total) * 100
            if (pct === 0) return null
            return (
              <div
                key={lvl}
                style={{ width: `${pct}%`, backgroundColor: COVERAGE_CONFIG[lvl].color }}
                className="flex items-center justify-center"
                title={`${COVERAGE_CONFIG[lvl].label}: ${counts[lvl]}`}
              >
                <span className="text-white text-xs font-bold">{counts[lvl]}</span>
              </div>
            )
          })}
        </div>
        <div className="flex gap-5 mt-2">
          {(['H', 'M', 'L'] as const).map(lvl => (
            <div key={lvl} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded-sm inline-block"
                style={{ backgroundColor: COVERAGE_CONFIG[lvl].color }}
              />
              <span className="text-xs text-gray-600">
                {COVERAGE_CONFIG[lvl].label}: <strong>{counts[lvl]}</strong>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-5 mb-4 flex-wrap">
        {(['H', 'M', 'L'] as const).map(lvl => (
          <div key={lvl} className="flex items-center gap-1.5">
            <span className="w-4 h-4 rounded" style={{ backgroundColor: COVERAGE_CONFIG[lvl].color }} />
            <span className="text-xs text-gray-600 font-medium">{COVERAGE_CONFIG[lvl].label} Coverage</span>
          </div>
        ))}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .recharts-surface { overflow: visible !important; }
          .recharts-wrapper { overflow: visible !important; }
          .recharts-responsive-container { overflow: visible !important; }
        }
      `}</style>

      {/* Bar chart */}
      <div style={{ width: '100%', height: chartHeight, overflow: 'visible' }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: 60, left: 260, bottom: 4 }}
            barCategoryGap="25%"
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} stroke="#F3F4F6" />
            <XAxis
              type="number"
              domain={[0, 3]}
              ticks={[1, 2, 3]}
              tickFormatter={v => ({ 1: 'L', 2: 'M', 3: 'H' }[v as 1|2|3] ?? '')}
              tick={{ fill: '#6B7280', fontSize: 13, fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={250}
              interval={0}
              tick={(props: any) => {
                const { x, y, payload } = props
                const words = (payload.value as string).split(' ')
                const lines: string[] = []
                let cur = ''
                words.forEach((w: string) => {
                  const test = cur ? `${cur} ${w}` : w
                  if (test.length * 6.5 > 240 && cur) { lines.push(cur); cur = w }
                  else cur = test
                })
                if (cur) lines.push(cur)
                const display = lines.slice(0, 2)
                if (lines.length > 2) display[1] = display[1].slice(0, 28) + '…'
                return (
                  <g>
                    {display.map((line, i) => (
                      <text
                        key={i}
                        x={x - 4}
                        y={y + i * 14 - (display.length - 1) * 7}
                        textAnchor="end"
                        fill="#374151"
                        fontSize={11}
                      >
                        {line}
                      </text>
                    ))}
                  </g>
                )
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={32}>
              {chartData.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-center text-gray-400 mt-1">Coverage Depth Level</p>

      {/* Summary stat cards */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        {(['H', 'M', 'L'] as const).map(lvl => (
          <div key={lvl} className="bg-gray-50 p-4 rounded-lg flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0"
              style={{ backgroundColor: COVERAGE_CONFIG[lvl].color }}
            >
              {lvl}
            </span>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts[lvl]}</p>
              <p className="text-xs text-gray-500">{COVERAGE_CONFIG[lvl].label} Coverage</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
